<template>
  <div
    class="row f-row"
    :class="isPrices ? `prices_chart_height` : `quantity_chart_height`"
  >
    <div
      class="col col-12 text-center"
      v-if="infoCharts.length === 0"
      style="width: 100%; top: calc(50% - 8px)!important; position: absolute;"
    >
      {{ noDataStr }}
    </div>

    <!-- Legends -->
    <CommodityAnalysisSelectors
      :Analytics="Analytics"
      :infoLegends="infoLegends"
      :isPrices="isPrices"
      :desiredOrderLegends="desiredOrderLegends"
      :sizedLegends="sizedLegends"
      @eventOnUserShow="onUserShow"
    />
    <!-- Chart  -->
    <div
      class="col"
      style="height:100%; width:100%"
      :class="`col-${valueOfSizeChart}`"
    >
      <div
        :id="`${idChart}-div`"
        style="width:100%"
        class="h-100 d-flex flex-column"
      >
        <svg :id="`svg-${idChart}`"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3HoverPlanning } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  currencyFormat,
  currencyFormat3,
  formatM,
  formatNumber2,
  formatPercent,
  formatPercentSm,
  lightBackground,
  textactive,
} from "@/_core/_helpers/_parametric_tools";
import { format, min as minD3, select } from "d3";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import { Chart } from "@/_core/refactor/Chart";
import { idxOfDate } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import CommodityAnalysisSelectors from "./CommodityAnalysisSelectors.vue";

export default {
  name: "CommodityAnalysis",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics(),
    },
    asset: { required: true, default: "commodity" },
    colorTheme: { required: true, type: Boolean, default: true },
    dataNest: { required: true, default: "" },
    idChart: { required: true, default: "" },
    infoCharts: {
      type: Array,
      required: true,
      default: () => [
        {
          label: "",
          nameEvent: "",
          nameData: "",
          colorBdr: "",
          colorBck: "",
          classNameIcon: "",
          show: "",
        },
      ],
    },
    infoLegends: {
      type: Array,
      required: true,
      default: () => [
        {
          label: "",
          colorBdr: "",
          colorBck: "",
          classNameIcon: "",
          show: "",
        },
      ],
    },
    isPrices: { default: false },
    isZoomY: { required: false, type: Boolean, default: false },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 },
    desiredOrderLegends: { required: false, type: Array, default: () => [] },
    hasMinMaxRect: { required: false, type: Boolean, default: false },
    customBarWidth: { required: false, type: Number, default: 0 },
    sizedLegends: {
      type: String,
      required: false,
      default: "1",
    },
  },
  components: {
    CommodityAnalysisSelectors,
  },
  data() {
    return {
      dateFormat: "%b %y",
      expandFRow: false,
      minMaxYAxis: [Infinity, -Infinity],
      currencyFormat: format(currencyFormat),
      chartBodyPlanning: null,
      derivativeSize: 14,
      lineData: [],
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
        logo: require("@/assets/images/symbol_blue.svg"),
      },
      isUpdatingTable: false,
      hoverZoomTexts: {
        xField: "date",
        yFields: [],
        customNameTexts: [],
        data: this.Analytics["rawData"][this.dataNest],
        xOffsets: [],
        rotates: [],
        yFieldsIncludes: [],
        positionText: "",
        formatText: formatPercentSm,
        textField: [],
        yField: [],
        maxHeight: 8,
      },
      hoverZoomScatterImg: {
        isShow: "",
        showFields: [],
        customName: [],
        dataHv: [],
      },
      xRange: [],
      filterDerivatives: [
        ["LFwd", "long", "offset short", "forward", "future"],
        ["SFwd", "short", "offset long", "forward", "future"],
        ["LCall", "long", "offset short", "call"],
        ["SCall", "short", "offset long", "call"],
        ["LPut", "long", "offset short", "put"],
        ["SPut", "short", "offset long", "put"],
      ],
      lCall: require("@/assets/images/lCall.svg"),
      lCallOff: require("@/assets/images/lCallA.svg"),
      lCallOn: require("@/assets/images/lCallH.svg"),
      lFwd: require("@/assets/images/lFwd.svg"),
      lFwdOff: require("@/assets/images/lFwdA.svg"),
      lFwdOn: require("@/assets/images/lFwdH.svg"),
      lPut: require("@/assets/images/lPut.svg"),
      lPutOff: require("@/assets/images/lPutA.svg"),
      lPutOn: require("@/assets/images/lPutH.svg"),
      showTooltipDeriv: true,
      sCall: require("@/assets/images/sCall.svg"),
      sCallOff: require("@/assets/images/sCallA.svg"),
      sCallOn: require("@/assets/images/sCallH.svg"),
      sFwd: require("@/assets/images/sFwd.svg"),
      sFwdOff: require("@/assets/images/sFwdA.svg"),
      sFwdOn: require("@/assets/images/sFwdH.svg"),
      sPut: require("@/assets/images/sPut.svg"),
      sPutOff: require("@/assets/images/sPutA.svg"),
      sPutOn: require("@/assets/images/sPutH.svg"),
    };
  },
  created() {
    this[`D3Chart${this.idChart}`] = new Chart(this.idChart);
    this[`D3Hover${this.idChart}`] = new D3HoverPlanning();
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  computed: {
    noDataStr() {
      // console.log("noDataStr")
      return this.infoCharts.length === 0 && "No data available";
    },
    dataDerivatives() {
      // console.log("dataDerivatives")
      return this.Analytics["rawData"]["zoomedDerivatives"];
    },
    showAve() {
      // console.log("showAve")
      return this.Analytics["userSelections"]["showAve"];
    },
    isNormalizeQty() {
      // console.log("isNormalizeQty")
      return this.Analytics["userSelections"]["normalizeQty"];
    },
    valueOfSizeChart() {
      // console.log("valueOfSizeChart")
      const sizedLegends = Number(this.sizedLegends);
      const columnTotal = 12;
      return columnTotal - sizedLegends;
    },
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    renderChartSignal() {
      if (this.Analytics["rawData"][this.dataNest].length > 0) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    formatVars: {
      handler() {
        if (!this.isUpdatingTable) {
          this.isUpdatingTable = true;
          setTimeout(() => {
            this.isUpdatingTable = false;
            this.onHoverChart();
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    deleteChart() {
      // console.log("deleteChart")
      select(`#${this.idChart}-div`)
        .selectAll(".tooltip")
        .remove();
      select(`#${this.idChart}-div`)
        .select("svg > *")
        .remove();
    },
    createBarChart() {
      // console.log("createBarChart")
      const rawData = this.Analytics["rawData"][this.dataNest];
      if (this.infoCharts.length === 0 || rawData.length === 0) return;

      // === set SVG canvas
      this[`D3Chart${this.idChart}`].setSVG({
        top: { perc: 0.5, px: 10 },
        right: { perc: 0.5, px: 55 },
        bottom: { perc: 0.5, px: 20 },
        left: { perc: 0.5, px: 1 },
      });

      if (
        this[`D3Chart${this.idChart}`].svg.height === 0 ||
        this[`D3Chart${this.idChart}`].svg.width === 0
      ) {
        return;
      }
      // === Get total sum of multiStacked bars
      // Get the sum of the bars stacked on top of each other
      if (this.dataNest !== "zoomedData") {
        this.calculateMultiStackedPercentages(rawData);
      }

      this.minMaxYAxis = [+Infinity, -Infinity];
      // === Set minMax Y axis
      const fixedHedged = (value) =>
        value.includes("hedged") || value.includes("fixed");
      let sumValuesData;
      if (this.idChart === "pDerivatives") {
        let keyNames = [
          "p_long call",
          "p_long forward",
          "p_long future",
          "p_long put",
          "p_short call",
          "p_short forward",
          "p_short future",
          "p_short put",
        ];
        keyNames.forEach((key) => {
          this.minMaxYAxis = this.processData.minMax(
            rawData,
            key,
            key,
            this.minMaxYAxis,
          );
        });
      } else if (this.idChart === "pStrategies") {
        this.infoCharts.forEach((infoChart) => {
          if (this.Analytics["userSelections"][infoChart.show]) {
            const nameData = infoChart.nameData;
            const valueAxis =
              this.isNormalizeQty && !this.isPrices
                ? `${nameData}Perc`
                : nameData;

            this.minMaxYAxis = this.processData.minMax(
              rawData,
              valueAxis,
              valueAxis,
              this.minMaxYAxis,
            );
          }
        });
      } else if (this.idChart === "rates") {
        // === auxData and Fields
        let fldForecast = this.Analytics["userSelections"]["fldBudget"];
        let fldFixed = this.Analytics["userSelections"]["fldFixed"];
        let fldMkt = this.Analytics["userSelections"]["fldMkt"];
        let fldBasis = this.Analytics["userSelections"]["fldBasis"];
        let fldNet = this.Analytics["userSelections"]["fldNet"];
        // === minMax
        if (this.idChart !== "pDerivatives") {
          this.setMinMaxNoHedges(
            fldForecast,
            fldFixed,
            fldMkt,
            fldNet,
            fldBasis,
          );
        }
        let filteredD = this.filterShownDerivatives();
        let len = this.Analytics["rawData"]["zoomedData"].length - 1;
        let inDat = +this.Analytics["rawData"]["zoomedData"][0].date;
        let endDat = +this.Analytics["rawData"]["zoomedData"][len].date;
        filteredD = filteredD.filter(
          (d) => +d.date >= inDat && +d.date <= endDat,
        );
        this.minMaxYAxis = this.processData.minMax(
          filteredD,
          "strike",
          "strike",
          this.minMaxYAxis,
        );
        this.minMaxYAxis = this.processData.minMax(
          filteredD,
          "barrier",
          "barrier",
          this.minMaxYAxis,
        );
        this.minMaxYAxis = this.processData.minMax(
          filteredD,
          "barrier_2",
          "barrier_2",
          this.minMaxYAxis,
        );
      } else {
        this.infoCharts.forEach((infoChart) => {
          if (this.Analytics["userSelections"][infoChart.show]) {
            const nameData = fixedHedged(infoChart.nameData)
              ? "fixedHedged"
              : infoChart.nameData;
            const valueAxis =
              this.isNormalizeQty && !this.isPrices
                ? `${nameData}Perc`
                : nameData;

            this.minMaxYAxis = this.processData.minMax(
              rawData,
              valueAxis,
              valueAxis,
              this.minMaxYAxis,
            );
          }
        });

        // Get sum of positive and negative values
        const positiveSums = [];
        const negativeSums = [];

        rawData.forEach((obj) => {
          let positiveSum = 0;
          let negativeSum = 0;

          this.infoCharts.forEach(({ nameData }) => {
            const nameData1 = fixedHedged(nameData) ? "fixedHedged" : nameData;
            const valueAxis =
              this.isNormalizeQty && !this.isPrices
                ? `${nameData1}Perc`
                : nameData1;

            const value = obj[valueAxis];
            if (!Number.isNaN(value) && value !== undefined) {
              if (value >= 0) {
                positiveSum += value;
              } else {
                negativeSum += value;
              }
            }
          });

          positiveSums.push(positiveSum);
          negativeSums.push(negativeSum);
        });
        const maxPositive = Math.max(...positiveSums);
        const minNegative = Math.min(0, ...negativeSums);

        sumValuesData =
          this.isPrices || this.idChart === "planning" ? 0 : maxPositive;

        this.minMaxYAxis = [
          Math.min(minNegative, this.minMaxYAxis[0], 0),
          Math.max(this.minMaxYAxis[1], 0),
        ];
      }

      let del =
        (this.idChart === "rates" ||
        this.idChart === "pDerivatives" ||
        this.idChart === "pStrategies"
          ? 0.05
          : 0.1) *
        (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      let del1 = this.minMaxYAxis[0] === 0 ? 0 : del;
      let del2 = this.minMaxYAxis[1] === 0 ? 0 : del;

      if (
        this.idChart !== "rates" &&
        this.idChart !== "pDerivatives" &&
        this.idChart !== "pStrategies"
      ) {
        this.minMaxYAxis = [
          Math.min(0, this.minMaxYAxis[0] - del1),
          Math.max(0, this.minMaxYAxis[1] + del2, sumValuesData + del2),
        ];
      } else {
        this.minMaxYAxis = [
          this.minMaxYAxis[0] - del1,
          this.minMaxYAxis[1] + del2,
        ];
      }

      this.minMaxYAxis = this.validateMinMaxYAxis(this);
      // === set axis
      this[`D3Chart${this.idChart}`].setAxis({
        xData: rawData,
        minMaxYAxis: this.minMaxYAxis,
        xFormat: this.dateFormat,
        nXTicks: 50,
        nYTicks: 4,
        addYFormat: this.isNormalizeQty && !this.isPrices,
        yAxisLeft: false,
        yFormat:
          this.isNormalizeQty && !this.isPrices
            ? formatPercentSm
            : currencyFormat3,
      });

      // ==== zoom setup
      this[`D3Chart${this.idChart}`].enableXZoom(
        `chartBody${this.idChart}`,
        this.idChart,
      );

      if (!this.expandFRow) {
        del = 0.03 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      } else {
        del = 0.1 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      }

      // === Add background chart
      this[`D3Chart${this.idChart}`].addBackgroundRect({
        type: "backgroundRect",
        xDates: rawData.map((d) => d.date),
        xCondition: rawData.map((d, i) => i % 2 !== 0),
        customName: "PLAN",
      });

      // === Create data elements
      const groupedData = {};

      const dataElemtsAve = {
        yFieldsWithPerc: [],
        barsNameFigure: [],
        barsNameFigureHvr: [],
        barsName: [],
        colorsFill: [],
        colorsBdr: [],
        showFields: [],
        isZeroFigures: [],
      };
      let containerFigures;
      const includesStacked = (d) => {
        return d.figureElements.includes("stacked2Bar")
          ? "stacked2Bar"
          : d.figureElements.includes("stacked1Bar")
          ? "stacked1Bar"
          : "";
      };

      this.infoCharts.forEach((d) => {
        if (!d?.typeFigure) return;
        const newNameData = this.getDynamicInfoPrices(d.nameData);
        const showFields =
          d.showArr && this.isPrices && Array.isArray(d.showArr)
            ? d.showArr
            : d.show;

        // Add hover elements for non-derived bars
        if (!d.classNameIcon || !d.classNameIcon?.includes("fa-eye")) {
          // The elements of the different figures are created grouped, for the hover and the figures
          if (!d.isDerivativeAve) {
            const nameFigure = d.typeFigure.nameFigure;

            const targetGroup = (groupedData[nameFigure] = groupedData[
              nameFigure
            ] || {
              yFieldsWithPerc: [],
              barsNameFigure: [],
              barsNameFigureHvr: [],
              barsName: [],
              colorsFill: [],
              colorsBdr: [],
              showFields: [],
              isZeroFigures: [],
            });

            targetGroup.barsName.push(d.nameEvent);
            targetGroup.barsNameFigure.push(
              `${includesStacked(d)}${d.nameEvent}`,
            );
            targetGroup.colorsFill.push(d.colorBck);
            targetGroup.colorsBdr.push(d.colorBdr);
            targetGroup.showFields.push(showFields);
            targetGroup.yFieldsWithPerc.push(
              newNameData ||
                (this.isNormalizeQty && !this.isPrices
                  ? `${d.nameData}Perc`
                  : d.nameData),
            );
            targetGroup.isZeroFigures.push(d?.typeFigure.toZero);

            targetGroup.barsNameFigureHvr.push(
              `${
                includesStacked(d)
                  ? includesStacked(d)
                  : d.figureElements === "stackedBar"
                  ? "stackedBar"
                  : "bar"
              }${d.nameEvent}`,
            );

            this.createStyleElementsHover({
              nameElement: `${d.figureElements}s${d.nameEvent}`,
              isPrices: this.isPrices,
              isZero: d.typeFigure.toZero || false,
            });
          } else {
            dataElemtsAve.barsName.push(d.nameEvent);
            dataElemtsAve.barsNameFigure.push(
              `${includesStacked(d)}${d.nameEvent}`,
            );
            dataElemtsAve.barsNameFigureHvr.push(
              `${
                includesStacked(d)
                  ? includesStacked(d)
                  : d.figureElements === "stackedBar"
                  ? "stackedBar"
                  : "bar"
              }${d.nameEvent}`,
            );
            dataElemtsAve.colorsFill.push(d.colorBck);
            dataElemtsAve.colorsBdr.push(d.colorBdr);
            dataElemtsAve.showFields.push(showFields);
            dataElemtsAve.yFieldsWithPerc.push(
              newNameData
                ? newNameData
                : this.isNormalizeQty && !this.isPrices
                ? `${d.nameData}Perc`
                : d.nameData,
            );
            dataElemtsAve.isZeroFigures.push(d?.typeFigure.toZero);
            containerFigures = `${d.figureElements}s${d.nameEvent}`;
            dataElemtsAve.unit = d.unit;
            // === Create hover styles for elements
            this.createStyleElementsHover({
              nameElement: containerFigures,
              isPrices: this.isPrices,
              isZero: d.typeFigure.toZero || false,
            });
          }
        }
      });

      // === Call and create figures elements
      let figureName;
      this.infoCharts.forEach((infoChart, i) => {
        const nameFigure = infoChart.typeFigure.nameFigure;
        if (figureName === "twoStackedBars" || infoChart.isDerivativeAve)
          return;

        // Create the type of figure that arrives in the data
        switch (nameFigure) {
          case "gBar":
            this.createGroupBar(rawData, infoChart);
            break;
          case "multipleStackedBars":
            {
              if (i === this.infoCharts.length - 1) {
                this.createMultipleStackedBars(
                  rawData,
                  groupedData[nameFigure],
                );
              }
            }
            break;
          case "twoStackedBars":
            this.createTwoStackedBars(rawData);
            break;
          default:
            break;
        }
        figureName = nameFigure;
      });

      // Join data from elements regardless of the shape type
      const mergedDataElements = Object.values(groupedData).reduce(
        (acc, obj) => {
          Object.keys(obj).forEach((key) => {
            acc[key] = [...(acc[key] || []), ...obj[key]];
          });
          return acc;
        },
        {},
      );

      // === Configuration hover elements
      let hoverConfigurationStacked = null;
      if (groupedData?.multipleStackedBars) {
        hoverConfigurationStacked = {
          showFields: groupedData.multipleStackedBars.showFields,
          data: rawData,
          customNameBars: groupedData.multipleStackedBars.barsNameFigure,
          yFields: groupedData.multipleStackedBars.yFieldsWithPerc,
          multipleYField: "normalizeQty",
          isElementZero: groupedData.multipleStackedBars.isZeroFigures,
        };
      }
      // Create hover elements for stacked bars
      const groupedDataWithoutStacked = Object.fromEntries(
        Object.entries(groupedData).filter(
          ([key]) => key !== "multipleStackedBars",
        ),
      );
      // Merge data elements for bars
      const mergedDataElementsBars = Object.values(
        groupedDataWithoutStacked,
      ).reduce((acc, obj) => {
        Object.keys(obj).forEach((key) => {
          acc[key] = [...(acc[key] || []), ...obj[key]];
        });
        return acc;
      }, {});

      const hoverConfigurationBars = {
        showFields: mergedDataElementsBars.showFields,
        data: rawData,
        customNameBars: mergedDataElementsBars.barsNameFigure,
        yFields: mergedDataElementsBars.yFieldsWithPerc,
        multipleYField: "normalizeQty",
        isElementZero: mergedDataElementsBars.isZeroFigures,
      };

      // ======= Create text elements =======
      this.createTextElements(mergedDataElements, this);

      // ======= Create minMax Rectangles =======
      const figuresElemsHover = [];

      if (this.hasMinMaxRect) {
        const figureElementRect = this.createMinMaxRect();
        if (figureElementRect) {
          figuresElemsHover.push(figureElementRect);
        }
      }
      // ======= Create derivatives =======
      if (
        (this.idChart === "rates" || this.idChart === "pDerivatives") &&
        !this.showAve
      ) {
        this.createAllDerivatives(true);
      }
      if (hoverConfigurationBars?.yFields?.length) {
        figuresElemsHover.push({
          type: "bars",
          data: [hoverConfigurationBars],
        });
      }

      if (
        hoverConfigurationStacked &&
        hoverConfigurationStacked?.yFields?.length
      ) {
        figuresElemsHover.push({
          type: "multipleStackedBars",
          data: [hoverConfigurationStacked],
        });
      }
      // Get data of elements for zoom Y
      if (this.hoverZoomTexts.yFields.length > 0) {
        figuresElemsHover.push({
          type: "texts",
          data: [this.hoverZoomTexts],
        });
      }
      if (this.hoverZoomScatterImg.customName.length > 0 && !this.showAve) {
        figuresElemsHover.push({
          type: "twoScatters",
          data: [this.hoverZoomScatterImg],
        });

        const showFields = [
          "showLFwd",
          "showSFwd",
          "showLCall",
          "showSCall",
          "showLPut",
          "showSPut",
        ];
        const customName = [
          "longFwds",
          "shortFwds",
          "longCalls",
          "shortCalls",
          "longPuts",
          "shortPuts",
        ];

        const scatterBarriers = {
          barrierData: this.dataDerivatives,
          dataSticks: [
            {
              showFields,
              customNameDash: [
                "LFWD",
                "SFWD",
                "LCALL",
                "SCALL",
                "LPUT",
                "SPUT",
              ],
              customName,
              data: this.hoverZoomScatterImg.dataHv,
            },
          ],
          dataRect: [
            {
              showFields,
              customName,
            },
          ],
          xRange: this.xRange,
          dateFld: "date",
        };
        figuresElemsHover.push({
          type: "scatterBarriers",
          data: scatterBarriers,
        });
      }

      if (this.showAve) {
        const hoverConfigurationBars = [
          {
            showFields: dataElemtsAve.showFields,
            data: this.Analytics["rawData"].derivatives,
            customNameBars: dataElemtsAve.barsNameFigure,
            yFields: dataElemtsAve.yFieldsWithPerc,
            multipleYField: "",
            isElementZero: false,
          },
        ];
        figuresElemsHover.push({ type: "bars", data: hoverConfigurationBars });
      }
      // === hover fx ===
      this.getStyleBands({ nameBand: `bands${this.idChart}` });
      this.getHoverFct({
        _this: this,
        figuresElemsHv: figuresElemsHover,
        svgNode: this[`D3Chart${this.idChart}`].svg,
        barsNameFigure: mergedDataElements.barsNameFigureHvr.concat(
          dataElemtsAve.barsNameFigureHvr,
        ),
        nameHover: this.idChart,
      });

      // Create tooltip text
      const yInsideTooltipWidth = 35;
      this[`D3Chart${this.idChart}`].createToolTipsHover(["y-inside"], {
        yInsideTooltipWidth,
        colorYTooltip: lightBackground,
      });
      this.createTextElements(mergedDataElements, this, false);

      // Get all scatter derivatives
      if (this.idChart === "rates" || this.idChart === "pDerivatives") {
        this.createAllDerivatives();
      }
      this[`svgNode${this.idChart}`] = this[`D3Chart${this.idChart}`];
    },
    calculateMultiStackedPercentages(rawData) {
      // console.log("calculateMultiStackedPercentages")
      if (!Array.isArray(rawData) || !this.infoCharts || !this.dataNest) {
        console.error("Invalid input data or configuration");
        return;
      }

      const multipleStackedFilters = this.infoCharts.filter(
        (dta) => dta.typeFigure?.nameFigure === "multipleStackedBars",
      );
      const minMaxFilters = this.infoCharts.filter(
        (dta) => dta.label === "Min" || dta.label === "Max",
      );
      const balanceFilters = this.infoCharts.filter(
        (dta) => dta.label === "Required",
      );

      const valuesPercent = rawData.map((dataPoint) => {
        const { positiveSum, negativeSum } = this.calculateSums(
          dataPoint,
          multipleStackedFilters,
        );

        const percentObj = this.calculatePercentages(
          dataPoint,
          multipleStackedFilters,
          positiveSum,
          negativeSum,
        );
        const minMaxPercentObj = this.calculatePercentages(
          dataPoint,
          minMaxFilters,
          positiveSum,
          negativeSum,
        );
        const balancePercentObj = this.calculatePercentages(
          dataPoint,
          balanceFilters,
          positiveSum,
          negativeSum,
        );

        return {
          ...dataPoint,
          ...percentObj,
          ...minMaxPercentObj,
          ...balancePercentObj,
        };
      });

      Object.assign(this.Analytics.rawData, { [this.dataNest]: valuesPercent });
    },
    calculateSums(dataPoint, filters) {
      // console.log("calculateSums")
      return filters.reduce(
        (sums, { nameData }) => {
          const value = Number(dataPoint[nameData]);
          if (!Number.isNaN(value)) {
            if (value >= 0) sums.positiveSum += value;
            else sums.negativeSum += Math.abs(value);
          }
          return sums;
        },
        { positiveSum: 0, negativeSum: 0 },
      );
    },
    calculatePercentages(dataPoint, filters, positiveSum, negativeSum) {
      // console.log("calculatePercentages")
      return filters.reduce((obj, { nameData }) => {
        const value = Number(dataPoint[nameData]);
        if (!Number.isNaN(value) && value !== undefined) {
          if (value >= 0 && positiveSum !== 0) {
            obj[`${nameData}Perc`] = value / positiveSum;
          } else if (value < 0 && negativeSum !== 0) {
            obj[`${nameData}Perc`] = value / negativeSum;
          } else {
            obj[`${nameData}Perc`] = undefined;
          }
        } else {
          obj[`${nameData}Perc`] = undefined;
        }
        return obj;
      }, {});
    },
    createTextElements(dataElements, _this, isCreateData = true) {
      // console.log("createTextElements")
      const rawData = _this.Analytics["rawData"][_this.dataNest];

      if (_this.isPrices) return;

      const chartId = this.idChart;
      const chartRef = this[`D3Chart${chartId}`];
      const chartBodyText = _this[`chartBody${chartId}text`];

      chartRef.enableXZoom(`chartBody${chartId}text`, `${chartId}text`);

      const filterYFields = dataElements.yFieldsWithPerc.filter(
        (field, i) =>
          this.infoCharts[i].showText &&
          field.includes(this.infoCharts[i].nameData) &&
          !field.includes("q_min") &&
          !field.includes("q_max"),
      );

      filterYFields.forEach((field, index) => {
        const textPerc = field.endsWith("Perc") ? field : `${field}Perc`;
        const element = this.infoCharts.find((info) =>
          field.includes(info.nameData),
        );

        if (!element || !this.Analytics.userSelections[element.show]) return;

        const offsetLeft = element.xOffsetLeft || 0;
        const isMultipleStackedBars =
          element.typeFigure.nameFigure === "multipleStackedBars";
        const isTwoStackBar = element.figureElements === "stacked2Bar";

        this.addPercElement(element);

        const customIndex =
          isTwoStackBar || isMultipleStackedBars
            ? (element.customYFieldsText || filterYFields).findIndex((f) =>
                field.includes(f),
              )
            : index;

        const yFields = isTwoStackBar
          ? element.customYFieldsText.map((f) =>
              f.endsWith("Perc") ? f : `${f}Perc`,
            )
          : null;

        const yField = isMultipleStackedBars
          ? field
          : isTwoStackBar
          ? yFields
          : "zeroFld";
        if (!isCreateData) {
          chartRef.addText({
            type: "text",
            data: rawData,
            yField,
            textField: textPerc,
            name: textPerc,
            fontSize: "1.1rem",
            xOffset: chartRef.svg.x.bandwidth() / 2 + offsetLeft,
            alignmentBaseline: isMultipleStackedBars ? "middle" : "ideographic",
            multiYFields: isMultipleStackedBars,
            textAnchor: isMultipleStackedBars
              ? "middle"
              : Math.sign(offsetLeft) === 1
              ? "start"
              : "end",
            textColor: "var(--textactive)",
            svgNodeAux: chartBodyText,
            formatText: formatPercentSm,
            yFields:
              isMultipleStackedBars || isTwoStackBar ? filterYFields : null,
            index: customIndex,
            yFieldsIncludes: isTwoStackBar
              ? element.customYFieldsText
              : isMultipleStackedBars
              ? filterYFields
              : null,
            positionText: isTwoStackBar ? "bottom" : "",
            toZero: true,
            maxHeight: isMultipleStackedBars ? 15 : 8,
          });
        } else {
          this.hoverZoomTexts.yFields.push(
            isMultipleStackedBars ? field : "zeroFld",
          );
          this.hoverZoomTexts.customNameTexts.push(textPerc);
          this.hoverZoomTexts.xOffsets.push(chartRef.svg.x.bandwidth() / 2);
          this.hoverZoomTexts.rotates.push(0);
          this.hoverZoomTexts.yFieldsIncludes = isTwoStackBar
            ? element.customYFieldsText
            : isMultipleStackedBars
            ? filterYFields
            : null;
          this.hoverZoomTexts.formatText = formatPercentSm;
          this.hoverZoomTexts.textField.push(textPerc);
          this.hoverZoomTexts.yField.push(yField);
          this.hoverZoomTexts.maxHeight = isMultipleStackedBars ? 15 : 8;
          this.hoverZoomTexts.positionText = isTwoStackBar ? "bottom" : "";
        }
      });
    },
    addPercElement(element) {
      // console.log("addPercElement")
      const normalizeQty = this.Analytics?.userSelections?.normalizeQty;

      if (element.customYFieldsText) {
        const isPercApplied = element.customYFieldsText.every((d) =>
          d.endsWith("Perc"),
        );

        if (normalizeQty && !isPercApplied) {
          // Apply percentage if user selection is set and not already applied
          element.customYFieldsText = element.customYFieldsText.map(
            (d) => `${d}Perc`,
          );
        } else if (!normalizeQty && isPercApplied) {
          // Remove percentage if user selection is not set and currently applied
          element.customYFieldsText = element.customYFieldsText.map((d) =>
            d.replace("Perc", ""),
          );
        }
      }
    },
    getHoverFct({ _this, figuresElemsHv, barsNameFigure, nameHover, svgNode }) {
      // console.log("getHoverFct")
      barsNameFigure = barsNameFigure.filter(
        (bar) => !bar.includes("q_min") && !bar.includes("q_max"),
      );
      _this[`D3Hover${_this.idChart}`].hoverFct(
        svgNode,
        _this.Analytics["rawData"][_this.dataNest],
        nameHover,
        formatNumber2,
        _this.currency,
        _this.Analytics["userSelections"]["normalizeQty"] && !this.isPrices
          ? formatPercent
          : currencyFormat3,
        _this.dateFormat,
        _this.currencyFormat,
        _this.budgetRate,
        formatPercent,
        false,
        formatM,
        _this.averages,
        false,
        _this.asset,
        _this.isFuture,
        _this.colorTheme,
        true,
        _this,
        "date",
        this.isZoomY,
        figuresElemsHv,
        "render",
        _this.Analytics["rawData"],
        true,
        barsNameFigure,
      );
    },
    getStyleBands({ nameBand }) {
      // console.log("getStyleBands")
      const styleCSS = document.createElement("style");
      styleCSS.innerHTML = `.${nameBand} rect {
        fill: var(--graylightbackground);
        stroke-width: 1px;
        fill-opacity: 0;
        stroke-opacity: 0;
        pointer-events: all;
        shape-rendering: crispEdges;
      }

    .${nameBand} .hoved {
        fill-opacity: 1;
      }
    `;

      document.head.appendChild(styleCSS);
    },
    createStyleElementsHover({ nameElement, isPrices = false, isZero = true }) {
      // console.log("createStyleElementsHover")
      const styleCSS = document.createElement("style");
      if (isPrices || !isZero) {
        styleCSS.innerHTML = `.${nameElement} .hoved {
          fill: var(--textactive) !important;
        }`;
      } else {
        styleCSS.innerHTML = `.${nameElement} .hoved {
          stroke-width: 2 !important;
          stroke: var(--textactive) !important;
        }`;
      }

      document.head.appendChild(styleCSS);
    },
    createTwoStackedBars(rawData) {
      // console.log("createTwoStackedBars")
      // Filter the elements from infoCharts that are of type twoStackedBars
      const filterDataElements = this.infoCharts.filter(
        (d) => d.typeFigure.nameFigure === "twoStackedBars",
      );
      const userSelect = this.Analytics["userSelections"];
      if (userSelect[filterDataElements[0].show]) {
        this[`D3Chart${this.idChart}`].addBars({
          type: "twoStackedBars",
          data: rawData,
          barWidth: 0.25,
          firstStackedBarInfo: {
            name: filterDataElements[0].oneStackedBar,
            color: filterDataElements[0].colorBdr,
            fillColor: filterDataElements[0].colorBck,
          },
          secondStackedBarInfo: {
            name: filterDataElements[1].twoStackedBar,
            color: filterDataElements[1].colorBdr,
            fillColor: filterDataElements[1].colorBck,
          },
          yField1: this.isNormalizeQty
            ? filterDataElements[0].oneStackedBar + "Perc"
            : filterDataElements[0].oneStackedBar,
          yField2: this.isNormalizeQty
            ? filterDataElements[1].twoStackedBar + "Perc"
            : filterDataElements[1].twoStackedBar,
          offset: filterDataElements[0].figureDetails.offset,
          idField: "date",
          addId: true,
        });
      }
    },
    createGroupBar(rawData, infoChart) {
      // console.log("createGroupBar")
      const bandWidth = this[`D3Chart${this.idChart}`].svg.x.bandwidth();
      const newNameData = this.getDynamicInfoPrices(infoChart.nameData);

      if (infoChart.typeFigure.nameFigure === "gBar") {
        const candleWidth =
          Math.floor(
            minD3([bandWidth * infoChart.figureDetails.barWidth, 1000000]) / 2,
          ) *
            2 +
          1;
        let isShow;

        if (
          infoChart.showArr &&
          this.isPrices &&
          Array.isArray(infoChart.showArr)
        ) {
          isShow = infoChart.showArr.every(
            (d) => this.Analytics.userSelections[d],
          );
        } else if ("show" in infoChart) {
          isShow = this.Analytics.userSelections[infoChart.show];
        } else {
          isShow = true;
        }

        if (isShow) {
          this[`D3Chart${this.idChart}`].addBars({
            type: "gBar",
            data: rawData,
            customName: infoChart.nameEvent,
            yField: newNameData
              ? newNameData
              : this.isNormalizeQty && !this.isPrices
              ? `${infoChart.nameData}Perc`
              : infoChart.nameData,
            fieldColor: infoChart.colorBdr,
            fieldBackground: infoChart.typeFigure.toZero
              ? infoChart.colorBck
              : "rgba(0, 0, 0, 0)",
            idField: "date",
            candleWidth,
            toZero: infoChart.typeFigure.toZero,
            xIsDate: false,
            offset: infoChart.figureDetails.offset,
            height: infoChart.figureDetails.height,
            lineWidth: infoChart.strokeWidth,
          });
        }
      }
    },
    getDynamicInfoPrices(d) {
      // console.log("getDynamicInfoPrices")
      let newNameData = "";
      if (this.isPrices && this.dataNest === "zoomedData") {
        let fldForecast = this.Analytics["userSelections"]["fldBudget"];
        let fldFixed = this.Analytics["userSelections"]["fldFixed"];
        let fldBasis = this.Analytics["userSelections"]["fldBasis"];
        let fldMkt = this.Analytics["userSelections"]["fldMkt"];
        let fldNet = this.Analytics["userSelections"]["fldNet"];
        if (
          fldForecast &&
          d === "p_forecast" &&
          fldForecast.includes("budgetP")
        ) {
          newNameData = fldForecast;
        }
        if (fldBasis && d === "p_ratio") {
          newNameData = fldBasis;
        }
        if (fldFixed && d === "p_fixed" && fldFixed.includes("fixedP")) {
          newNameData = fldFixed;
        }
        if (fldMkt && d === "p_market" && fldMkt.includes("marketP")) {
          newNameData = fldMkt;
        }
        if (fldNet && d === "p_net" && fldNet.includes("netP")) {
          newNameData = fldNet;
        }
      }
      return newNameData;
    },
    createMultipleStackedBars(rawData, dataElements) {
      // console.log("createMultipleStackedBars")
      const barWidth = this.customBarWidth ? this.customBarWidth : 0.9;
      this[`D3Chart${this.idChart}`].addBars({
        type: "multipleStackedBars",
        data: rawData,
        barWidth,
        nameBars: dataElements.barsName,
        colors: dataElements.colorsBdr,
        fillColors: dataElements.colorsFill,
        xField: "date",
        yFields: dataElements.yFieldsWithPerc,
        addId: true,
        idField: "date",
      });
    },
    createAllDerivatives(isConfigDatahover = false) {
      // console.log("createAllDerivatives")
      this.xRange = this.Analytics["rawData"][this.dataNest].map((d) => d.date);

      const filterData = {};
      this.filterDerivatives.forEach(
        ([name, buySell1, buySell2, derivative1, derivative2]) => {
          filterData[`data${name}`] = this.filterDataDerivatives(
            buySell1,
            buySell2,
            derivative1,
            derivative2 ? derivative2 : derivative1,
          );
        },
      );
      // === create derivatives charts ===
      if (
        this.dataDerivatives.length > 0 &&
        !this.Analytics["userSelections"]["showAve"] &&
        [
          "showLFwd",
          "showSFwd",
          "showLCall",
          "showSCall",
          "showLPut",
          "showSPut",
        ].includes(this.Analytics["userSelections"])
      ) {
        let dates = this.dataDerivatives.map((d) => +d.date);
        this.dataDerivatives.forEach(function(d) {
          d.historicalIndex = idxOfDate(dates, +d.trade_date);
        });
      } else {
        this.dataDerivatives.forEach(function(d) {
          d.historicalIndex = -1;
        });
      }

      const derivatives = [
        {
          data: filterData[`dataLFwd`],
          short: "LFwd",
          long: "longFwds",
        },
        {
          data: filterData[`dataSFwd`],
          short: "SFwd",
          long: "shortFwds",
        },
        {
          data: filterData[`dataLCall`],
          short: "LCall",
          long: "longCalls",
        },
        {
          data: filterData[`dataSCall`],
          short: "SCall",
          long: "shortCalls",
        },
        {
          data: filterData[`dataLPut`],
          short: "LPut",
          long: "longPuts",
        },
        {
          data: filterData[`dataSPut`],
          short: "SPut",
          long: "shortPuts",
        },
      ];

      if (!this.Analytics["userSelections"]["showAve"]) {
        derivatives.forEach(({ data, long, short }) => {
          if (isConfigDatahover) {
            let {
              dataHv,
              // eslint-disable-next-line no-unused-vars
              isShow,
              customName,
              showFields,
            } = this.hoverZoomScatterImg;

            isShow = "showAve";
            dataHv.push(data);
            showFields.push(`show${short}`);
            customName.push(long);
          } else {
            this.getScatterImg(data, short, long);
          }
        });
      } else {
        this.infoCharts.forEach((infoChart) => {
          if (infoChart.isDerivativeAve) {
            this.createGroupBar(
              this.Analytics["rawData"].derivatives,
              infoChart,
            );
          }
        });
      }
    },
    filterDataDerivatives(
      buySell1,
      buySell2,
      derivative1,
      derivative2 = derivative1,
    ) {
      // console.log("filterDataDerivatives")
      return this.Analytics.rawData.filteredData.filter(
        (d) =>
          (d.buy_sell === buySell1 || d.buy_sell === buySell2) &&
          (d.derivative === derivative1 || d.derivative === derivative2),
      );
    },
    getScatterImg(dataShow, derivativeShort, derivativeLong) {
      // console.log("getScatterImg")
      // === chartBody scatter
      this[`D3Chart${this.idChart}`].enableXZoom(
        `chartBody${this.idChart}scatter`,
        `${this.idChart}scatter`,
      );
      const chartBody = this[`D3Chart${this.idChart}`].svgAux;

      if (this.Analytics["userSelections"][`show${derivativeShort}`]) {
        const shortCamelCase =
          derivativeShort.charAt(0).toLowerCase() + derivativeShort.slice(1);

        this[`D3Chart${this.idChart}`].addScatter({
          type: "scatterImg",
          data: dataShow,
          dateFld: "date",
          groupField: derivativeLong,
          customName: derivativeShort.toUpperCase(),
          numberFormat: formatNumber2,
          currencyFormat: this.currencyFormat,
          dateFormat: "%d %b %y",
          baseC: this.currency,
          imgURL: this[shortCamelCase],
          imgURLOff: this[`${shortCamelCase}Off`],
          xRange: this.xRange,
          derivativeSize: this.derivativeSize,
          lFField: "longFwds",
          sFField: "shortFwds",
          lCField: "longCalls",
          sCField: "shortCalls",
          lPField: "longPuts",
          sPField: "shortPuts",
          showTooltipDeriv: this.showTooltipDeriv,
          isFuture: this.Analytics["rawData"]["zoomedData"].length === 0,
          asset: this.asset,
          adjustTooltip: this.adjustTooltip,
          colorTheme: this.colorTheme,
          isAnalytics: true,
          chartBody,
        });
      }
    },
    filterShownDerivatives() {
      // console.log("filterShownDerivatives")
      let res = [];
      const userSelection = this.Analytics["userSelections"];
      this.filterDerivatives.forEach(
        ([name, buySell1, buySell2, derivative1, derivative2]) => {
          if (userSelection[`show${name}`]) {
            const filterDataDerivatives = this.filterDataDerivatives(
              buySell1,
              buySell2,
              derivative1,
              derivative2 ? derivative2 : derivative1,
            );
            if (filterDataDerivatives.length > 0) {
              res.push(...filterDataDerivatives);
            }
          }
        },
      );
      return res;
    },
    setMinMaxNoHedges(fldForecast, fldFixed, fldMkt, fldNet, fldBasis) {
      // console.log("setMinMaxNoHedges")
      this.minMaxYAxis = [Infinity, -Infinity];
      if (this.Analytics["userSelections"]["showForecast"]) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldForecast,
          fldForecast,
          this.minMaxYAxis,
        );
      }
      if (
        this.Analytics["userSelections"]["showFixedFutures"] ||
        this.Analytics["userSelections"]["showFixedBasis"]
      ) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldFixed,
          fldFixed,
          this.minMaxYAxis,
        );
      }
      if (this.Analytics["userSelections"]["showMarket"]) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldMkt,
          fldMkt,
          this.minMaxYAxis,
        );
      }
      if (this.Analytics["userSelections"]["showNet"]) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldNet,
          fldNet,
          this.minMaxYAxis,
        );
      }
      if (
        this.Analytics["userSelections"]["showFixedBasis"] &&
        this.Analytics["userSelections"]["includeRatio"]
      ) {
        this.minMaxYAxis = this.processData.minMax(
          this.Analytics["rawData"]["zoomedData"],
          fldBasis,
          fldBasis,
          this.minMaxYAxis,
        );
      }
    },
    validateMinMaxYAxis(_this) {
      // console.log("validateMinMaxYAxis")
      let minMaxY = _this.minMaxYAxis;
      if (minMaxY[0] === minMaxY[1]) {
        minMaxY = [0, minMaxY[0] * 2];
      }
      return minMaxY;
    },
    addMinMaxRect(d, chartRef, chartBodyRect) {
      // console.log("addMinMaxRect")
      if (d.q_max) {
        const max =
          this.isNormalizeQty && !this.isPrices ? d.q_maxPerc : d.q_max;
        const min =
          this.isNormalizeQty && !this.isPrices ? d.q_minPerc : d.q_min;

        // Define line data for easier updates during zoom
        const lineData = [
          {
            type: "customLine",
            strokeColor: textactive(this.colorTheme),
            xMin: d.date,
            xMax: d.date,
            yMin: max,
            yMax: max,
            name: `max-${+d.date}`,
            dashArray: "4",
            isBandWidthMax: false,
            reductionLeft: 5,
            reductionRight: -5,
            strokeWidth: 2,
          },
          {
            type: "customLine",
            strokeColor: textactive(this.colorTheme),
            xMin: d.date,
            xMax: d.date,
            yMin: min,
            yMax: min,
            name: `min-${+d.date}`,
            dashArray: "4",
            isBandWidthMax: false,
            reductionLeft: 5,
            reductionRight: -5,
            strokeWidth: 2,
          },
          {
            type: "customLine",
            strokeColor: textactive(this.colorTheme),
            xMin: d.date,
            xMax: d.date,
            yMin: min,
            yMax: max,
            name: `minMax-${+d.date}`,
            reductionLeft: 5,
            reductionRight: 5,
            strokeWidth: 2,
          },
          {
            type: "customLine",
            strokeColor: textactive(this.colorTheme),
            xMin: d.date,
            xMax: d.date,
            yMin: min,
            yMax: max,
            name: `minMaxRight-${+d.date}`,
            isBandWidthMax: false,
            isBandWidthMin: false,
            reductionLeft: -5,
            reductionRight: -5,
            strokeWidth: 2,
          },
        ];

        // Store line data for use in figuresZoomY
        this.lineData = this.lineData || [];
        this.lineData.push(...lineData);

        // Initial rendering of lines
        lineData.forEach((line) => {
          chartRef.addLines({ ...line, svgAux: chartBodyRect });
        });
      }
    },
    createMinMaxRect() {
      // console.log("createMinMaxRect")
      this[`D3Chart${this.idChart}`].enableXZoom(
        `chartBody${this.idChart}rect`,
        `${this.idChart}rect`,
      );
      const chartBody = this[`D3Chart${this.idChart}`].svgAux;

      this.Analytics["rawData"][this.dataNest].forEach((infoChart) => {
        this.addMinMaxRect(
          infoChart,
          this[`D3Chart${this.idChart}`],
          chartBody,
        );
      });

      return {
        type: "customLine",
        data: this.lineData,
      };
    },
    resetHoverZoomTexts() {
      // console.log("resetHoverZoomTexts")
      this.hoverZoomTexts = {
        xField: "date",
        yFields: [],
        customNameTexts: [],
        data: this.Analytics["rawData"][this.dataNest],
        xOffsets: [],
        rotates: [],
        yFieldsIncludes: [],
        positionText: "",
        formatText: formatPercentSm,
        textField: [],
        yField: [],
        maxHeight: 8,
      };
    },
    onUserShow(str) {
      // console.log("onUserShow")
      this.resetHoverZoomTexts();
      this.$emit("eventOnUserShow", str);
    },
    onHoverChart() {
      // console.log("onHoverChart")
      this.$emit("onTableFormat", this.formatVars.userHovered);
    },
    callWidth() {
      // console.log("callWidth")
      if (this.Analytics["rawData"][this.dataNest].length > 0) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
  },
};
</script>

<style>
.textHover {
  display: none;
}

.fa:hover + .textHover {
  display: inline-block;
}

.tooltipIcon {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 5px;
  border-radius: 3px;
  display: none;
}

.container-legends_chart_quantity {
  overflow: auto;
  height: inherit;
}
</style>
